/**
 * CJK-aware line wrapping (pure, no Phaser). Phaser's own wrap only breaks on spaces, which does
 * nothing for Chinese prose; this breaks per character with basic 禁则 (kinsoku) rules so that a
 * line never starts with closing punctuation and never ends with an opening bracket/quote.
 */

export type Measure = (s: string) => number;

/** Cannot start a line. */
export const NO_LINE_START = '，。、！？；：…—”』」》）〕】〉〕·,.!?;:)]}';
/** Cannot end a line. */
export const NO_LINE_END = '“『「《（〔【〈([{';

function isCjk(ch: string): boolean {
  const c = ch.codePointAt(0) ?? 0;
  return c >= 0x2e80;
}

/**
 * Wraps `text` into lines no wider than `maxWidth` using `measure` (pixel width of a string).
 * Explicit `\n` always breaks. Latin words are kept together when possible.
 */
export function wrapText(text: string, maxWidth: number, measure: Measure): string[] {
  const out: string[] = [];
  for (const para of text.split('\n')) {
    if (para === '') { out.push(''); continue; }
    const chars = Array.from(para);
    let line = '';
    let i = 0;
    while (i < chars.length) {
      const ch = chars[i] ?? '';
      // Group latin runs (a "word") so we do not split English mid-word when avoidable.
      let unit = ch;
      if (!isCjk(ch) && ch !== ' ' && !NO_LINE_START.includes(ch)) {
        let j = i + 1;
        while (j < chars.length) {
          const cj = chars[j] ?? '';
          if (isCjk(cj) || cj === ' ' || NO_LINE_START.includes(cj) || NO_LINE_END.includes(cj)) break;
          unit += cj; j++;
        }
        i = j;
      } else {
        i++;
      }
      const candidate = line + unit;
      if (line.length === 0 || measure(candidate) <= maxWidth) {
        line = candidate;
        continue;
      }
      // Overflow: apply kinsoku before committing the break.
      if (NO_LINE_START.includes(unit)) {
        // Pull the previous character down with the punctuation (unless the line would empty).
        const prevChars = Array.from(line);
        if (prevChars.length > 1) {
          const moved = prevChars.pop() ?? '';
          out.push(prevChars.join(''));
          line = moved + unit;
        } else {
          line = candidate; // degenerate: keep on the line
        }
        continue;
      }
      const lineChars = Array.from(line);
      const last = lineChars[lineChars.length - 1] ?? '';
      if (NO_LINE_END.includes(last) && lineChars.length > 1) {
        lineChars.pop();
        out.push(lineChars.join(''));
        line = last + unit;
        continue;
      }
      out.push(line.replace(/ +$/, ''));
      line = unit === ' ' ? '' : unit;
    }
    out.push(line.replace(/ +$/, ''));
  }
  return out;
}

/** Convenience: wrapped lines joined with newlines for a Text object with wrap disabled. */
export function wrapToString(text: string, maxWidth: number, measure: Measure): string {
  return wrapText(text, maxWidth, measure).join('\n');
}
